import { isBefore, subMilliseconds } from 'date-fns';
import { type GuardArgs } from 'xstate5/dist/declarations/src/guards';

import { OrderPaymentStatus } from '@sg/graphql-schema';

import { type PaymentStatePollingContext } from './payment-state-polling-machine.context';
import { PaymentStatePollingEvent } from './payment-state-polling-machine.events';

// ─────────────────────────────────────────────────────────────────────────────

const checkIfPaymentIsPending: Guard = ({ context }) => {
  const paymentStateStatus = context.paymentState?.paymentStateStatus;
  const paymentStateExpiration = context.paymentState?.paymentStateExpiration;
  const { paymentStateExpirationTolerance } = context.pollingConfiguration;

  const paymentStateExpirationDate = new Date(Number(paymentStateExpiration));
  const expirationDate = subMilliseconds(new Date(), paymentStateExpirationTolerance);

  const isCreated = paymentStateStatus === OrderPaymentStatus.Created;
  const isApproved = paymentStateStatus === OrderPaymentStatus.Approved;
  const isPending = isCreated || isApproved;
  const isExpired = isBefore(paymentStateExpirationDate, expirationDate);

  return isPending && !isExpired;
};

const checkIfPaymentStateFulfilled: Guard = ({ context }) => {
  const paymentStateStatus = context.paymentState?.paymentStateStatus;
  const isFulfilled = paymentStateStatus === OrderPaymentStatus.Fulfilled;

  return isFulfilled;
};

const checkIfPaymentStateFailed: Guard = ({ context }) => {
  const paymentStateStatus = context.paymentState?.paymentStateStatus;
  const isFailed = paymentStateStatus === OrderPaymentStatus.Failed;
  const hasError = Boolean(context.paymentState.paymentStateError);

  return isFailed || hasError;
};

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentStatePollingGuards = {
  checkIfPaymentIsPending,
  checkIfPaymentStateFulfilled,
  checkIfPaymentStateFailed,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Guard = (args: GuardArgs<PaymentStatePollingContext, PaymentStatePollingEvent>) => boolean;
